import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { motion, AnimatePresence } from 'framer-motion';
import { Users, Plus, Search, Upload, Mail, Phone, Building, Tag, Trash2, Download, FileSpreadsheet, FileText, ExternalLink, X, Eye, MessageCircle, PhoneCall, Play, Clock, CheckCircle, XCircle, AlertCircle, Loader2, Pause, Volume2, ChevronDown, ChevronUp, TrendingUp } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import * as XLSX from 'xlsx';
import CallModal from '../components/CallModal';
import BulkWhatsAppModal from '../components/BulkWhatsAppModal';
import WhatsAppComposeModal from '../components/WhatsAppComposeModal';
import { useBusinessContent } from '../contexts/BusinessContentContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// CallRecordingCard Component with audio player and keyword highlighting
const CallRecordingCard = ({ call, formatDuration, formatDateTime, getCallStatusBadge }) => {
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [showTranscript, setShowTranscript] = useState(false);
  const audioRef = useRef(null);
  
  // Keyword highlighting function
  const highlightKeywords = (text) => {
    if (!text) return null;
    
    const keywords = {
      interested: 'text-green-600 bg-green-100 px-1 rounded',
      'not interested': 'text-red-600 bg-red-100 px-1 rounded',
      price: 'text-yellow-600 bg-yellow-100 px-1 rounded',
      visit: 'text-blue-600 bg-blue-100 px-1 rounded',
      buy: 'text-green-600 bg-green-100 px-1 rounded',
      budget: 'text-yellow-600 bg-yellow-100 px-1 rounded',
      appointment: 'text-blue-600 bg-blue-100 px-1 rounded',
      callback: 'text-purple-600 bg-purple-100 px-1 rounded',
      yes: 'text-green-600 bg-green-100 px-1 rounded',
      no: 'text-red-600 bg-red-100 px-1 rounded',
    };
    
    let result = text;
    Object.entries(keywords).forEach(([word, className]) => {
      const regex = new RegExp(`\\b(${word})\\b`, 'gi');
      result = result.replace(regex, `<span class="${className}">$1</span>`);
    });
    
    return <span dangerouslySetInnerHTML={{ __html: result }} />;
  };
  
  const handlePlayPause = () => {
    if (!audioRef.current) return;
    if (isPlaying) {
      audioRef.current.pause();
    } else {
      audioRef.current.play();
    }
    setIsPlaying(!isPlaying);
  };
  
  const handleTimeUpdate = () => {
    if (audioRef.current) {
      setCurrentTime(audioRef.current.currentTime);
    }
  };
  
  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };
  
  return (
    <Card className="hover:shadow-md transition-shadow bg-[#0a0f1e] border-[#1e3a5f30]">
      <CardContent className="p-4">
        {/* Header */}
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-blue-500/20 flex items-center justify-center">
              <PhoneCall className="w-5 h-5 text-blue-400" />
            </div>
            <div>
              <div className="flex items-center gap-2">
                {getCallStatusBadge(call.status)}
                <span className="text-sm text-gray-400 flex items-center gap-1">
                  <Clock className="w-3 h-3" />
                  {formatDuration(call.duration)}
                </span>
              </div>
              <p className="text-xs text-gray-500 mt-1">
                {formatDateTime(call.created_at)} • {call.script_type || 'General'} script
              </p>
            </div>
          </div>
          {call.score_impact && (
            <div className={`flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${
              call.score_impact > 0 ? 'bg-green-500/20 text-green-400' : 'bg-red-500/20 text-red-400'
            }`}>
              <TrendingUp className={`w-3 h-3 ${call.score_impact < 0 ? 'rotate-180' : ''}`} />
              {call.score_impact > 0 ? '+' : ''}{call.score_impact}
            </div>
          )}
        </div>
        
        {/* Simple Audio Player */}
        {call.recording_url && (
          <div className="bg-[#111827] border border-[#1e3a5f] rounded-lg p-3 mb-3">
            <div className="flex items-center gap-3">
              <button
                onClick={handlePlayPause}
                className="w-10 h-10 rounded-full bg-[#00d4aa] hover:bg-[#00b894] flex items-center justify-center transition-colors"
              >
                {isPlaying ? (
                  <Pause className="w-5 h-5 text-[#0a0f1e]" />
                ) : (
                  <Play className="w-5 h-5 text-[#0a0f1e] ml-0.5" />
                )}
              </button>
              
              {/* Progress Bar */}
              <div className="flex-1 h-2 bg-[#1e3a5f] rounded-full overflow-hidden">
                <div 
                  className="h-full bg-[#00d4aa] transition-all"
                  style={{ 
                    width: audioRef.current?.duration 
                      ? `${(currentTime / audioRef.current.duration) * 100}%` 
                      : '0%' 
                  }}
                />
              </div>
              
              <span className="text-xs text-gray-400 min-w-[45px] text-right">
                {formatTime(currentTime)} / {formatTime(call.duration || 0)}
              </span>
              
              <Volume2 className="w-4 h-4 text-gray-400" />
            </div>
            
            <audio
              ref={audioRef}
              src={call.recording_url}
              onTimeUpdate={handleTimeUpdate}
              onEnded={() => setIsPlaying(false)}
              className="hidden"
            />
          </div>
        )}
        
        {/* Transcript Toggle */}
        {call.transcript && (
          <button
            onClick={() => setShowTranscript(!showTranscript)}
            className="flex items-center gap-2 text-sm text-[#00d4aa] hover:text-[#00b894] transition-colors mb-2"
          >
            <FileText className="w-4 h-4" />
            {showTranscript ? 'Hide' : 'View'} Transcript
            {showTranscript ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
          </button>
        )}
        
        {/* Transcript with Keyword Highlighting */}
        {showTranscript && call.transcript && (
          <div className="mt-2 p-3 bg-[#111827] rounded-lg border border-[#1e3a5f] max-h-48 overflow-y-auto">
            <p className="text-xs font-medium text-gray-500 mb-2">Full Transcript</p>
            <p className="text-sm text-gray-300 leading-relaxed whitespace-pre-wrap">
              {highlightKeywords(call.transcript)}
            </p>
          </div>
        )}
        
        {/* AI Summary */}
        {call.summary && (
          <div className="mt-3 p-3 bg-indigo-500/10 rounded-lg border border-indigo-500/20">
            <p className="text-xs font-medium text-indigo-400 mb-1 flex items-center gap-1">
              <span>🤖</span> AI Summary
            </p>
            <p className="text-sm text-gray-300">{call.summary}</p>
            {call.next_action && (
              <p className="text-xs text-indigo-400 mt-2">
                <strong>Next Action:</strong> {call.next_action}
              </p>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

// Stage colors for dynamic pipeline
const STAGE_COLORS = [
  'bg-blue-100 text-blue-800',
  'bg-yellow-100 text-yellow-800',
  'bg-purple-100 text-purple-800',
  'bg-indigo-100 text-indigo-800',
  'bg-orange-100 text-orange-800',
  'bg-green-100 text-green-800',
  'bg-gray-100 text-gray-800',
  'bg-pink-100 text-pink-800',
  'bg-teal-100 text-teal-800',
];

// Default pipeline stages (fallback)
const DEFAULT_PIPELINE_STAGES = [
  { value: 'new', label: 'New Lead', color: 'bg-blue-100 text-blue-800' },
  { value: 'contacted', label: 'Contacted', color: 'bg-yellow-100 text-yellow-800' },
  { value: 'qualified', label: 'Qualified', color: 'bg-purple-100 text-purple-800' },
  { value: 'demo', label: 'Demo Booked', color: 'bg-indigo-100 text-indigo-800' },
  { value: 'negotiation', label: 'Negotiation', color: 'bg-orange-100 text-orange-800' },
  { value: 'won', label: 'Closed Won', color: 'bg-green-100 text-green-800' },
  { value: 'lost', label: 'Closed Lost', color: 'bg-gray-100 text-gray-800' },
];

const INDUSTRY_TAGS = ['SaaS', 'E-commerce', 'Healthcare', 'Finance', 'Education', 'Real Estate', 'Manufacturing', 'Retail'];
const SOURCE_TAGS = ['Website', 'LinkedIn', 'Referral', 'Cold Outreach', 'Event', 'Advertisement', 'Partnership'];

const LeadsEnhanced = () => {
  const navigate = useNavigate();
  
  // Get dynamic pipeline stages from business content
  const { getPipelineStages, getBusinessTypeName, businessType } = useBusinessContent();
  
  // Build pipeline stages from business content
  const PIPELINE_STAGES = React.useMemo(() => {
    const stages = getPipelineStages();
    if (Array.isArray(stages) && stages.length > 0) {
      return stages.map((stage, idx) => ({
        value: stage.toLowerCase().replace(/[^a-z0-9]/g, '_'),
        label: stage,
        color: STAGE_COLORS[idx % STAGE_COLORS.length]
      }));
    }
    return DEFAULT_PIPELINE_STAGES;
  }, [getPipelineStages, businessType]);
  
  const [leads, setLeads] = useState([]);
  const [filteredLeads, setFilteredLeads] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [loading, setLoading] = useState(true);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [showImportDialog, setShowImportDialog] = useState(false);
  const [csvText, setCsvText] = useState('');
  const [uploadedFile, setUploadedFile] = useState(null);
  const [previewData, setPreviewData] = useState([]);
  const [importing, setImporting] = useState(false);
  
  // Pagination State
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 20,
    total: 0,
    pages: 0,
    has_next: false,
    has_prev: false
  });
  
  // Lead Detail Modal State
  const [selectedLead, setSelectedLead] = useState(null);
  const [showLeadDetail, setShowLeadDetail] = useState(false);
  const [activeTab, setActiveTab] = useState('info');
  const [leadDocuments, setLeadDocuments] = useState([]);
  const [loadingDocs, setLoadingDocs] = useState(false);
  const [leadCalls, setLeadCalls] = useState([]);
  const [loadingCalls, setLoadingCalls] = useState(false);
  
  // Call Modal State
  const [showCallModal, setShowCallModal] = useState(false);
  const [callModalLead, setCallModalLead] = useState(null);
  
  // Bulk Selection State
  const [selectedLeadIds, setSelectedLeadIds] = useState([]);
  const [showBulkWhatsApp, setShowBulkWhatsApp] = useState(false);
  
  // WhatsApp Compose Modal
  const [showWhatsAppCompose, setShowWhatsAppCompose] = useState(false);
  const [whatsAppLead, setWhatsAppLead] = useState(null);
  
  // Virtual Scrolling State
  const [allLeads, setAllLeads] = useState([]);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const listRef = useRef(null);
  const containerRef = useRef(null);
  
  const [newLead, setNewLead] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    industry: '',
    tags: [],
  });

  useEffect(() => {
    fetchLeads();
  }, []);

  useEffect(() => {
    let filtered = leads;

    if (searchQuery) {
      filtered = filtered.filter(
        (lead) =>
          lead.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
          lead.email.toLowerCase().includes(searchQuery.toLowerCase()) ||
          (lead.company && lead.company.toLowerCase().includes(searchQuery.toLowerCase()))
      );
    }

    if (filterStatus !== 'all') {
      filtered = filtered.filter((lead) => lead.status === filterStatus);
    }

    setFilteredLeads(filtered);
  }, [searchQuery, filterStatus, leads]);

  const fetchLeads = async (page = 1, append = false) => {
    try {
      if (page === 1) {
        setLoading(true);
      } else {
        setLoadingMore(true);
      }
      
      const token = localStorage.getItem('auth_token');
      const params = new URLSearchParams({
        page: page.toString(),
        limit: '50'  // Fetch 50 at a time for virtual scrolling
      });
      if (filterStatus !== 'all') {
        params.append('lead_status', filterStatus);
      }
      if (searchQuery) {
        params.append('search', searchQuery);
      }
      
      const res = await axios.get(`${BACKEND_URL}/api/leads?${params.toString()}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      
      const newLeads = res.data.leads || [];
      
      if (append) {
        setLeads(prev => [...prev, ...newLeads]);
        setAllLeads(prev => [...prev, ...newLeads]);
      } else {
        setLeads(newLeads);
        setAllLeads(newLeads);
      }
      
      if (res.data.pagination) {
        setPagination(res.data.pagination);
        setHasMore(res.data.pagination.has_next);
      } else {
        setHasMore(newLeads.length === 50);
      }
    } catch (error) {
      console.error('Failed to fetch leads:', error);
      toast.error('Failed to load leads');
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  };
  
  // Load more leads for infinite scroll
  const loadMoreLeads = useCallback(() => {
    if (!loadingMore && hasMore && pagination.has_next) {
      fetchLeads(pagination.page + 1, true);
    }
  }, [loadingMore, hasMore, pagination]);
  
  // Handle scroll for infinite loading
  const handleScroll = useCallback(({ scrollOffset, scrollDirection }) => {
    if (listRef.current && scrollDirection === 'forward') {
      const list = listRef.current;
      const scrollHeight = filteredLeads.length * 200; // Approximate row height
      const scrollBottom = scrollOffset + 600; // Container height
      
      // Load more when 80% scrolled
      if (scrollBottom > scrollHeight * 0.8 && hasMore && !loadingMore) {
        loadMoreLeads();
      }
    }
  }, [filteredLeads.length, hasMore, loadingMore, loadMoreLeads]);
  
  // Refetch when filters change
  useEffect(() => {
    const timer = setTimeout(() => {
      fetchLeads(1);
    }, 300);
    return () => clearTimeout(timer);
  }, [filterStatus, searchQuery]);

  const handleAddLead = async () => {
    if (!newLead.name || !newLead.email) {
      toast.error('Name and email are required');
      return;
    }

    try {
      const token = localStorage.getItem('auth_token');
      const res = await axios.post(
        `${BACKEND_URL}/api/leads`,
        newLead,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      // Store latest lead ID for Welcome Tour
      if (res.data.id) {
        localStorage.setItem('latest_lead_id', res.data.id);
        localStorage.setItem('has_added_lead', 'true');
      }
      
      toast.success(`Lead added with score ${res.data.score}/100! Automation started!`);
      setShowAddDialog(false);
      setNewLead({ name: '', email: '', phone: '', company: '', industry: '', tags: [] });
      fetchLeads();
    } catch (error) {
      console.error('Failed to add lead:', error);
      toast.error('Failed to add lead');
    }
  };

  const handleImportCSV = async () => {
    try {
      if (!csvText || csvText.trim().length === 0) {
        toast.error('Please paste CSV data');
        return;
      }

      const lines = csvText.trim().split('\n').filter(line => line.trim().length > 0);
      
      if (lines.length < 2) {
        toast.error('CSV must have at least a header row and one data row');
        return;
      }

      const headers = lines[0].split(',').map((h) => h.trim().toLowerCase());
      
      // Validate required headers
      if (!headers.includes('name') || !headers.includes('email')) {
        toast.error('CSV must include at least "name" and "email" columns');
        return;
      }

      const leadsData = [];

      for (let i = 1; i < lines.length; i++) {
        const line = lines[i].trim();
        if (!line) continue; // Skip empty lines
        
        const values = line.split(',').map((v) => v.trim());
        const leadObj = {};
        headers.forEach((header, index) => {
          leadObj[header] = values[index] || '';
        });
        
        // Skip if no email
        if (leadObj.email) {
          leadsData.push(leadObj);
        }
      }

      if (leadsData.length === 0) {
        toast.error('No valid leads found in CSV. Make sure each row has an email.');
        return;
      }

      const token = localStorage.getItem('auth_token');
      const res = await axios.post(
        `${BACKEND_URL}/api/leads/import-csv`,
        { leads: leadsData },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      toast.success(`Successfully imported ${res.data.count} leads!`);
      setShowImportDialog(false);
      setCsvText('');
      fetchLeads();
    } catch (error) {
      console.error('Failed to import CSV:', error);
      toast.error(error.response?.data?.detail || 'Failed to import CSV');
    }
  };

  const handleFileUpload = (event) => {
    const file = event.target.files[0];
    if (!file) return;

    setUploadedFile(file);

    const reader = new FileReader();

    reader.onload = (e) => {
      try {
        const data = e.target.result;
        let workbook;

        // Check file type and parse accordingly
        if (file.name.endsWith('.csv')) {
          // Parse CSV
          workbook = XLSX.read(data, { type: 'binary' });
        } else if (file.name.endsWith('.xlsx') || file.name.endsWith('.xls')) {
          // Parse Excel
          workbook = XLSX.read(data, { type: 'array' });
        } else {
          toast.error('Please upload a CSV or Excel file (.csv, .xlsx, .xls)');
          return;
        }

        // Get first sheet
        const sheetName = workbook.SheetNames[0];
        const worksheet = workbook.Sheets[sheetName];

        // Convert to JSON
        const jsonData = XLSX.utils.sheet_to_json(worksheet, { defval: '' });

        if (jsonData.length === 0) {
          toast.error('File is empty or has no data');
          return;
        }

        // Validate required columns
        const firstRow = jsonData[0];
        const hasName = 'name' in firstRow || 'Name' in firstRow;
        const hasEmail = 'email' in firstRow || 'Email' in firstRow;

        if (!hasName || !hasEmail) {
          toast.error('Excel/CSV must have "name" and "email" columns');
          return;
        }

        // Normalize column names to lowercase
        const normalizedData = jsonData.map(row => {
          const normalized = {};
          Object.keys(row).forEach(key => {
            normalized[key.toLowerCase()] = row[key];
          });
          return normalized;
        });

        setPreviewData(normalizedData);
        toast.success(`Loaded ${normalizedData.length} rows from file`);
      } catch (error) {
        console.error('File parse error:', error);
        toast.error('Failed to parse file. Please check the format.');
      }
    };

    // Read file based on type
    if (file.name.endsWith('.csv')) {
      reader.readAsText(file);
    } else {
      reader.readAsArrayBuffer(file);
    }
  };

  const handleImportFromFile = async () => {
    if (previewData.length === 0) {
      toast.error('No data to import. Please upload a file first.');
      return;
    }

    setImporting(true);

    try {
      const token = localStorage.getItem('auth_token');
      
      console.log(`Importing ${previewData.length} leads...`);
      
      const res = await axios.post(
        `${BACKEND_URL}/api/leads/import-csv`,
        { leads: previewData },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      console.log('Import response:', res.data);

      const { count, skipped, total } = res.data;

      if (count > 0) {
        if (skipped > 0) {
          toast.success(`Imported ${count} new leads. Skipped ${skipped} duplicates.`);
        } else {
          toast.success(`Successfully imported all ${count} leads!`);
        }
      } else if (skipped > 0) {
        toast.warning(`All ${skipped} leads already exist. No new leads imported.`);
      } else {
        toast.error('No leads imported. Please check your data.');
      }
      
      // Close dialog and refresh
      setShowImportDialog(false);
      setCsvText('');
      setUploadedFile(null);
      setPreviewData([]);
      
      // Refresh leads list
      await fetchLeads();
      
    } catch (error) {
      console.error('Import error:', error);
      toast.error(error.response?.data?.detail || 'Failed to import leads');
    } finally {
      setImporting(false);
    }
  };

  const handleUpdateStatus = async (leadId, newStatus) => {
    try {
      const token = localStorage.getItem('auth_token');
      await axios.put(
        `${BACKEND_URL}/api/leads/${leadId}`,
        { status: newStatus },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success('Lead status updated!');
      fetchLeads();
    } catch (error) {
      console.error('Failed to update status:', error);
      toast.error('Failed to update status');
    }
  };

  const handleDeleteLead = async (leadId) => {
    try {
      const token = localStorage.getItem('auth_token');
      await axios.delete(`${BACKEND_URL}/api/leads/${leadId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      toast.success('Lead deleted successfully');
      fetchLeads();
    } catch (error) {
      console.error('Failed to delete lead:', error);
      toast.error('Failed to delete lead');
    }
  };

  const downloadCSVTemplate = () => {
    const template = 'name,email,phone,company,industry,tags,status,notes\nSample Name,john@example.com,1234567890,Acme Corp,SaaS,Website,new,Sample lead';
    const blob = new Blob([template], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'leads_template.csv';
    a.click();
  };

  const getStatusColor = (status) => {
    const stage = PIPELINE_STAGES.find((s) => s.value === status);
    return stage ? stage.color : 'bg-gray-100 text-gray-800';
  };

  // Lead Detail Functions
  const openLeadDetail = async (lead) => {
    setSelectedLead(lead);
    setShowLeadDetail(true);
    setActiveTab('info');
    await Promise.all([
      fetchLeadDocuments(lead.id),
      fetchLeadCalls(lead.id)
    ]);
  };

  const fetchLeadDocuments = async (leadId) => {
    setLoadingDocs(true);
    try {
      const token = localStorage.getItem('auth_token');
      const res = await axios.get(`${BACKEND_URL}/api/documents/by-lead/${leadId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setLeadDocuments(res.data.documents || []);
    } catch (error) {
      console.error('Failed to fetch lead documents:', error);
      setLeadDocuments([]);
    } finally {
      setLoadingDocs(false);
    }
  };

  const fetchLeadCalls = async (leadId) => {
    setLoadingCalls(true);
    try {
      const token = localStorage.getItem('auth_token');
      const res = await axios.get(`${BACKEND_URL}/api/vapi/calls/${leadId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setLeadCalls(res.data.calls || []);
    } catch (error) {
      console.error('Failed to fetch lead calls:', error);
      setLeadCalls([]);
    } finally {
      setLoadingCalls(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const formatDateTime = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatDuration = (seconds) => {
    if (!seconds || seconds === 0) return '0:00';
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const getCallStatusBadge = (status) => {
    switch (status) {
      case 'completed':
      case 'in_progress':
        return <Badge className="bg-green-100 text-green-800"><CheckCircle className="w-3 h-3 mr-1" /> Answered</Badge>;
      case 'failed':
      case 'no-answer':
        return <Badge className="bg-red-100 text-red-800"><XCircle className="w-3 h-3 mr-1" /> No Answer</Badge>;
      case 'busy':
        return <Badge className="bg-yellow-100 text-yellow-800"><AlertCircle className="w-3 h-3 mr-1" /> Busy</Badge>;
      case 'calling':
      case 'initiating':
        return <Badge className="bg-blue-100 text-blue-800"><Phone className="w-3 h-3 mr-1 animate-pulse" /> Calling</Badge>;
      default:
        return <Badge className="bg-gray-100 text-gray-800">{status}</Badge>;
    }
  };

  const openCallModal = (lead) => {
    if (!lead.phone) {
      toast.error('Add phone number to lead first');
      return;
    }
    setCallModalLead(lead);
    setShowCallModal(true);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-muted-foreground">Loading leads...</p>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="leads-page" className="space-y-8">
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
        <div>
          <h1 className="text-4xl md:text-5xl font-heading font-bold tracking-tight mb-2">{getBusinessTypeName()} CRM</h1>
          <p className="text-lg text-muted-foreground">Manage leads with auto-scoring & pipeline tracking</p>
        </div>
        <div className="flex gap-2">
          <Dialog open={showImportDialog} onOpenChange={setShowImportDialog}>
            <DialogTrigger asChild>
              <Button data-testid="import-csv-btn" variant="outline">
                <Upload className="w-4 h-4 mr-2" />
                Import Excel/CSV
              </Button>
            </DialogTrigger>
            <DialogContent data-testid="import-csv-dialog" className="max-w-3xl max-h-[80vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>Import Leads from Excel or CSV</DialogTitle>
              </DialogHeader>
              <div className="space-y-6 mt-4">
                {/* File Upload Section */}
                <div className="border-2 border-dashed rounded-lg p-6 text-center">
                  <FileSpreadsheet className="w-12 h-12 mx-auto text-muted-foreground mb-3" />
                  <h3 className="font-semibold mb-2">Upload Excel or CSV File</h3>
                  <p className="text-sm text-muted-foreground mb-4">
                    Supports .xlsx, .xls, and .csv files
                  </p>
                  <input
                    type="file"
                    accept=".csv,.xlsx,.xls"
                    onChange={handleFileUpload}
                    className="hidden"
                    id="file-upload"
                  />
                  <label htmlFor="file-upload">
                    <Button type="button" variant="outline" asChild>
                      <span className="cursor-pointer">
                        <Upload className="w-4 h-4 mr-2" />
                        Choose File
                      </span>
                    </Button>
                  </label>
                  {uploadedFile && (
                    <p className="text-sm text-green-600 mt-2">
                      ✓ {uploadedFile.name} loaded ({previewData.length} rows)
                    </p>
                  )}
                </div>

                {/* Preview Section */}
                {previewData.length > 0 && (
                  <div>
                    <h4 className="font-semibold mb-2">Preview ({previewData.length} leads)</h4>
                    <div className="border rounded-lg overflow-auto max-h-60">
                      <table className="w-full text-sm">
                        <thead className="bg-muted">
                          <tr>
                            <th className="px-3 py-2 text-left">Name</th>
                            <th className="px-3 py-2 text-left">Email</th>
                            <th className="px-3 py-2 text-left">Company</th>
                            <th className="px-3 py-2 text-left">Phone</th>
                          </tr>
                        </thead>
                        <tbody>
                          {previewData.slice(0, 5).map((lead, index) => (
                            <tr key={index} className="border-t">
                              <td className="px-3 py-2">{lead.name}</td>
                              <td className="px-3 py-2">{lead.email}</td>
                              <td className="px-3 py-2">{lead.company || '-'}</td>
                              <td className="px-3 py-2">{lead.phone || '-'}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                      {previewData.length > 5 && (
                        <p className="text-xs text-center text-muted-foreground py-2">
                          ...and {previewData.length - 5} more rows
                        </p>
                      )}
                    </div>
                    <Button
                      onClick={handleImportFromFile}
                      disabled={importing}
                      className="w-full mt-4 bg-indigo-600 hover:bg-indigo-700"
                      data-testid="import-from-file-btn"
                    >
                      {importing ? (
                        <>
                          <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                          Importing...
                        </>
                      ) : (
                        <>
                          <Upload className="w-4 h-4 mr-2" />
                          Import {previewData.length} Leads
                        </>
                      )}
                    </Button>
                  </div>
                )}

                {/* Divider */}
                <div className="relative">
                  <div className="absolute inset-0 flex items-center">
                    <div className="w-full border-t"></div>
                  </div>
                  <div className="relative flex justify-center text-sm">
                    <span className="bg-background px-2 text-muted-foreground">OR</span>
                  </div>
                </div>

                {/* Manual Paste Section */}
                <div className="flex justify-between items-center">
                  <p className="text-sm text-muted-foreground">
                    Paste CSV text manually (with headers)
                  </p>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={downloadCSVTemplate}
                    data-testid="download-template-btn"
                  >
                    <Download className="w-4 h-4 mr-2" />
                    Download Template
                  </Button>
                </div>
                <textarea
                  data-testid="csv-input"
                  className="w-full h-48 p-4 border rounded-lg font-mono text-sm"
                  placeholder="Paste CSV with headers (required: name, email)

Example:
name,email,phone,company,industry,tags,status
Sample Name,john@example.com,1234567890,Acme Corp,SaaS,Website,new
Jane Smith,jane@startup.io,9876543210,Startup Inc,E-commerce,LinkedIn,contacted"
                  value={csvText}
                  onChange={(e) => setCsvText(e.target.value)}
                />
                <Button data-testid="import-csv-submit-btn" onClick={handleImportCSV} className="w-full">
                  <Upload className="w-4 h-4 mr-2" />
                  Import from Pasted Text
                </Button>
              </div>
            </DialogContent>
          </Dialog>

          <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
            <DialogTrigger asChild>
              <Button data-testid="add-lead-btn" className="bg-indigo-600 hover:bg-indigo-700">
                <Plus className="w-4 h-4 mr-2" />
                Add Lead
              </Button>
            </DialogTrigger>
            <DialogContent data-testid="add-lead-dialog">
              <DialogHeader>
                <DialogTitle>Add New Lead</DialogTitle>
              </DialogHeader>
              <div className="space-y-4 mt-4">
                <Input
                  data-testid="lead-name-input"
                  placeholder="Name *"
                  value={newLead.name}
                  onChange={(e) => setNewLead({ ...newLead, name: e.target.value })}
                />
                <Input
                  data-testid="lead-email-input"
                  type="email"
                  placeholder="Email *"
                  value={newLead.email}
                  onChange={(e) => setNewLead({ ...newLead, email: e.target.value })}
                />
                <Input
                  data-testid="lead-phone-input"
                  placeholder="Phone"
                  value={newLead.phone}
                  onChange={(e) => setNewLead({ ...newLead, phone: e.target.value })}
                />
                <Input
                  data-testid="lead-company-input"
                  placeholder="Company"
                  value={newLead.company}
                  onChange={(e) => setNewLead({ ...newLead, company: e.target.value })}
                />
                <Select value={newLead.industry} onValueChange={(val) => setNewLead({ ...newLead, industry: val })}>
                  <SelectTrigger data-testid="lead-industry-select">
                    <SelectValue placeholder="Select Industry" />
                  </SelectTrigger>
                  <SelectContent>
                    {INDUSTRY_TAGS.map((industry) => (
                      <SelectItem key={industry} value={industry}>
                        {industry}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <div>
                  <label className="text-sm font-medium mb-2 block">Tags (Source/Category)</label>
                  <div className="flex flex-wrap gap-2 mb-2">
                    {SOURCE_TAGS.map((tag) => (
                      <Badge
                        key={tag}
                        variant={newLead.tags.includes(tag) ? 'default' : 'outline'}
                        className="cursor-pointer"
                        onClick={() => {
                          if (newLead.tags.includes(tag)) {
                            setNewLead({ ...newLead, tags: newLead.tags.filter((t) => t !== tag) });
                          } else {
                            setNewLead({ ...newLead, tags: [...newLead.tags, tag] });
                          }
                        }}
                      >
                        {tag}
                      </Badge>
                    ))}
                  </div>
                </div>
                <Button data-testid="submit-lead-btn" onClick={handleAddLead} className="w-full">
                  Add Lead (Auto-Scored)
                </Button>
              </div>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* Search and Filters */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                data-testid="search-leads-input"
                placeholder="Search leads by name, email, or company..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10"
              />
            </div>
            <Select value={filterStatus} onValueChange={setFilterStatus}>
              <SelectTrigger className="w-full md:w-48" data-testid="filter-status-select">
                <SelectValue placeholder="Filter by Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Stages</SelectItem>
                {PIPELINE_STAGES.map((stage) => (
                  <SelectItem key={stage.value} value={stage.value}>
                    {stage.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Bulk Selection Bar */}
      {selectedLeadIds.length > 0 && (
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="flex items-center justify-between p-4 bg-emerald-900/20 border border-emerald-700/30 rounded-lg"
        >
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-emerald-500/20 flex items-center justify-center">
              <Users className="w-5 h-5 text-emerald-400" />
            </div>
            <div>
              <span className="font-semibold text-emerald-300">{selectedLeadIds.length}</span>
              <span className="text-emerald-300/70"> lead{selectedLeadIds.length !== 1 ? 's' : ''} selected</span>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setSelectedLeadIds([])}
              className="border-emerald-700/50 text-emerald-300"
            >
              <X className="w-4 h-4 mr-1" />
              Clear
            </Button>
            <Button
              size="sm"
              onClick={() => setShowBulkWhatsApp(true)}
              className="bg-green-600 hover:bg-green-500 text-white"
            >
              <MessageCircle className="w-4 h-4 mr-2" />
              Send WhatsApp
            </Button>
          </div>
        </motion.div>
      )}

      {/* Select All / Deselect All */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <input
            type="checkbox"
            checked={selectedLeadIds.length === filteredLeads.length && filteredLeads.length > 0}
            onChange={(e) => {
              if (e.target.checked) {
                setSelectedLeadIds(filteredLeads.map(l => l.id));
              } else {
                setSelectedLeadIds([]);
              }
            }}
            className="w-4 h-4 rounded border-gray-300"
          />
          <span className="text-sm text-muted-foreground">
            {selectedLeadIds.length === filteredLeads.length && filteredLeads.length > 0 ? 'Deselect All' : 'Select All'}
          </span>
        </div>
        <span className="text-sm text-muted-foreground">
          {filteredLeads.length} lead{filteredLeads.length !== 1 ? 's' : ''} found
        </span>
      </div>

      {/* Pipeline Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-4">
        {PIPELINE_STAGES.map((stage) => {
          const count = leads.filter((l) => l.status === stage.value).length;
          return (
            <Card key={stage.value} className="hover:shadow-md transition-shadow">
              <CardContent className="pt-6 text-center">
                <div className="text-2xl font-bold font-heading">{count}</div>
                <div className="text-xs text-muted-foreground mt-1">{stage.label}</div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      {/* Leads Grid - Simple Scrollable */}
      <div 
        className="relative overflow-y-auto" 
        style={{ maxHeight: 'calc(100vh - 400px)', minHeight: '400px' }}
      >
        {filteredLeads.length > 0 ? (
          <>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {filteredLeads.map((lead, index) => (
                <motion.div
                  key={lead.id}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: Math.min(index * 0.02, 0.3) }}
                >
                  <Card 
                    data-testid={`lead-card-${index}`} 
                    className={`hover:shadow-lg transition-shadow ${selectedLeadIds.includes(lead.id) ? 'ring-2 ring-emerald-500' : ''}`}
                  >
                    <CardHeader>
                      <div className="flex justify-between items-start">
                        <div className="flex items-start gap-3">
                          <input
                            type="checkbox"
                            checked={selectedLeadIds.includes(lead.id)}
                            onChange={(e) => {
                              if (e.target.checked) {
                                setSelectedLeadIds([...selectedLeadIds, lead.id]);
                              } else {
                                setSelectedLeadIds(selectedLeadIds.filter(id => id !== lead.id));
                              }
                            }}
                            onClick={(e) => e.stopPropagation()}
                            className="w-4 h-4 mt-1.5 rounded border-gray-300 cursor-pointer"
                          />
                          <div className="flex-1">
                            <CardTitle className="text-xl font-heading">{lead.name}</CardTitle>
                            <Select
                              value={lead.status}
                              onValueChange={(val) => handleUpdateStatus(lead.id, val)}
                            >
                              <SelectTrigger className={`mt-2 ${getStatusColor(lead.status)}`}>
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                {PIPELINE_STAGES.map((stage) => (
                                  <SelectItem key={stage.value} value={stage.value}>
                                    {stage.label}
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>
                        </div>
                        <Button
                          data-testid={`delete-lead-${index}`}
                          variant="ghost"
                          size="icon"
                          onClick={() => handleDeleteLead(lead.id)}
                          className="text-red-500 hover:text-red-700 hover:bg-red-50"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </CardHeader>
                    <CardContent className="space-y-2">
                      <div className="flex items-center gap-2 text-sm text-muted-foreground">
                        <Mail className="w-4 h-4" />
                        <span className="truncate">{lead.email}</span>
                      </div>
                      {lead.phone && (
                        <div className="flex items-center gap-2 text-sm text-muted-foreground">
                          <Phone className="w-4 h-4" />
                          <span>{lead.phone}</span>
                        </div>
                      )}
                      {lead.company && (
                        <div className="flex items-center gap-2 text-sm text-muted-foreground">
                          <Building className="w-4 h-4" />
                          <span>{lead.company}</span>
                        </div>
                      )}
                      {lead.tags && lead.tags.length > 0 && (
                        <div className="flex items-center gap-2 flex-wrap mt-2">
                          <Tag className="w-4 h-4 text-muted-foreground" />
                          {lead.tags.slice(0, 2).map((tag, i) => (
                            <Badge key={i} variant="secondary" className="text-xs">
                              {tag}
                            </Badge>
                          ))}
                          {lead.tags.length > 2 && (
                            <Badge variant="outline" className="text-xs">+{lead.tags.length - 2}</Badge>
                          )}
                        </div>
                      )}
                      <div className="mt-2 pt-2 border-t">
                        <div className="flex justify-between items-center mb-1">
                          <span className="text-xs font-medium">Score</span>
                          <span className="text-xs font-bold">{lead.score || 0}/100</span>
                        </div>
                        <div className="w-full bg-muted rounded-full h-1.5">
                          <div
                            className="bg-indigo-600 h-1.5 rounded-full transition-all"
                            style={{ width: `${lead.score || 0}%` }}
                          />
                        </div>
                      </div>
                      <div className="flex gap-2 mt-2">
                        <Button
                          variant="outline"
                          size="sm"
                          className="flex-1"
                          onClick={() => openLeadDetail(lead)}
                          data-testid={`view-lead-${index}`}
                        >
                          <Eye className="w-4 h-4 mr-1" />
                          View
                        </Button>
                        {lead.phone && (
                          <>
                            <Button
                              size="sm"
                              className="bg-blue-600 hover:bg-blue-700 text-white"
                              onClick={(e) => {
                                e.stopPropagation();
                                openCallModal(lead);
                              }}
                              data-testid={`call-lead-${index}`}
                              title="AI Voice Call"
                            >
                              <PhoneCall className="w-4 h-4" />
                            </Button>
                            <Button
                              size="sm"
                              className="bg-green-600 hover:bg-green-700 text-white"
                              onClick={(e) => {
                                e.stopPropagation();
                                const phone = lead.phone.replace(/[^0-9]/g, '');
                                const cleanPhone = phone.startsWith('91') ? phone : '91' + phone;
                                const businessType = localStorage.getItem('business_type') || 'general';
                                const templates = {
                                  real_estate: `Hi ${lead.name}! Thank you for your property enquiry. I would love to help you find your dream property! When is a good time to talk?`,
                                  insurance: `Hi ${lead.name}! Thank you for your insurance enquiry. I can help you find the best policy for your needs! Shall we connect today?`,
                                  training: `Hi ${lead.name}! Thank you for your course enquiry! I would love to tell you more about our programs.`,
                                  healthcare: `Hi ${lead.name}! Thank you for reaching out! We would love to help you.`,
                                  automobile: `Hi ${lead.name}! Thank you for your enquiry! Would you like to schedule a test drive?`,
                                  general: `Hi ${lead.name}! Thank you for your enquiry! I would love to connect!`
                                };
                                const message = templates[businessType] || templates.general;
                                window.open(`https://wa.me/${cleanPhone}?text=${encodeURIComponent(message)}`, '_blank');
                                toast.success(`Opening WhatsApp for ${lead.name}`);
                              }}
                              data-testid={`whatsapp-lead-${index}`}
                              title="WhatsApp"
                            >
                              <MessageCircle className="w-4 h-4" />
                            </Button>
                          </>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                </motion.div>
              ))}
            </div>
            {/* Load More Button */}
            {pagination.has_next && (
              <div className="flex justify-center py-6">
                <Button 
                  variant="outline" 
                  onClick={() => fetchLeads(pagination.page + 1, true)}
                  disabled={loadingMore}
                  className="gap-2"
                >
                  {loadingMore ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <Users className="w-4 h-4" />
                  )}
                  {loadingMore ? 'Loading...' : `Load More (${pagination.total - leads.length} remaining)`}
                </Button>
              </div>
            )}
          </>
        ) : (
          <Card>
            <CardContent className="py-12 text-center">
              <Users className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
              <p className="text-muted-foreground">
                {searchQuery || filterStatus !== 'all'
                  ? 'No leads found matching your filters'
                  : 'No leads yet. Add your first lead or import from CSV!'}
              </p>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Lead Detail Modal */}
      <AnimatePresence>
        {showLeadDetail && selectedLead && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4"
            onClick={() => setShowLeadDetail(false)}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-hidden"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Header */}
              <div className="p-6 border-b bg-gradient-to-r from-indigo-500 to-purple-600">
                <div className="flex items-center justify-between">
                  <div>
                    <h2 className="text-2xl font-bold text-white">{selectedLead.name}</h2>
                    <p className="text-white/80 text-sm">{selectedLead.email}</p>
                  </div>
                  <button 
                    onClick={() => setShowLeadDetail(false)}
                    className="p-2 hover:bg-white/20 rounded-lg transition-colors"
                  >
                    <X className="w-5 h-5 text-white" />
                  </button>
                </div>
              </div>

              {/* Tabs */}
              <div className="flex border-b">
                <button
                  onClick={() => setActiveTab('info')}
                  className={`flex-1 py-3 px-4 text-sm font-medium transition-colors ${
                    activeTab === 'info' 
                      ? 'text-indigo-600 border-b-2 border-indigo-600' 
                      : 'text-gray-500 hover:text-gray-700'
                  }`}
                >
                  Lead Info
                </button>
                <button
                  onClick={() => setActiveTab('calls')}
                  className={`flex-1 py-3 px-4 text-sm font-medium transition-colors flex items-center justify-center gap-2 ${
                    activeTab === 'calls' 
                      ? 'text-indigo-600 border-b-2 border-indigo-600' 
                      : 'text-gray-500 hover:text-gray-700'
                  }`}
                  data-testid="calls-tab"
                >
                  <PhoneCall className="w-4 h-4" />
                  Calls
                  {leadCalls.length > 0 && (
                    <Badge variant="secondary" className="text-xs">{leadCalls.length}</Badge>
                  )}
                </button>
                <button
                  onClick={() => setActiveTab('documents')}
                  className={`flex-1 py-3 px-4 text-sm font-medium transition-colors flex items-center justify-center gap-2 ${
                    activeTab === 'documents' 
                      ? 'text-indigo-600 border-b-2 border-indigo-600' 
                      : 'text-gray-500 hover:text-gray-700'
                  }`}
                  data-testid="documents-tab"
                >
                  <FileText className="w-4 h-4" />
                  Documents
                  {leadDocuments.length > 0 && (
                    <Badge variant="secondary" className="text-xs">{leadDocuments.length}</Badge>
                  )}
                </button>
              </div>

              {/* Content */}
              <div className="p-6 overflow-y-auto max-h-[60vh]">
                {activeTab === 'info' && (
                  <div className="space-y-4">
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="text-sm font-medium text-gray-500">Status</label>
                        <Badge className={`block mt-1 ${getStatusColor(selectedLead.status)}`}>
                          {PIPELINE_STAGES.find(s => s.value === selectedLead.status)?.label || selectedLead.status}
                        </Badge>
                      </div>
                      <div>
                        <label className="text-sm font-medium text-gray-500">Lead Score</label>
                        <p className="text-2xl font-bold text-indigo-600">{selectedLead.score}/100</p>
                      </div>
                    </div>

                    {selectedLead.phone && (
                      <div>
                        <label className="text-sm font-medium text-gray-500">Phone</label>
                        <p className="text-gray-900">{selectedLead.phone}</p>
                      </div>
                    )}

                    {selectedLead.company && (
                      <div>
                        <label className="text-sm font-medium text-gray-500">Company</label>
                        <p className="text-gray-900">{selectedLead.company}</p>
                      </div>
                    )}

                    {selectedLead.industry && (
                      <div>
                        <label className="text-sm font-medium text-gray-500">Industry</label>
                        <p className="text-gray-900">{selectedLead.industry}</p>
                      </div>
                    )}

                    {selectedLead.tags?.length > 0 && (
                      <div>
                        <label className="text-sm font-medium text-gray-500">Tags</label>
                        <div className="flex flex-wrap gap-2 mt-1">
                          {selectedLead.tags.map((tag, i) => (
                            <Badge key={i} variant="secondary">{tag}</Badge>
                          ))}
                        </div>
                      </div>
                    )}

                    {/* Quick Action Buttons */}
                    {selectedLead.phone && (
                      <div className="pt-4 border-t border-gray-200">
                        <label className="text-sm font-medium text-gray-500 mb-2 block">Quick Actions</label>
                        <div className="flex gap-2">
                          <Button
                            size="sm"
                            className="bg-green-600 hover:bg-green-700 text-white flex-1"
                            onClick={() => {
                              const phone = selectedLead.phone.replace(/[^0-9]/g, '');
                              const cleanPhone = phone.startsWith('91') ? phone : '91' + phone;
                              const businessType = localStorage.getItem('business_type') || 'general';
                              const templates = {
                                real_estate: `Hi ${selectedLead.name}! Thank you for your property enquiry. 🏠`,
                                insurance: `Hi ${selectedLead.name}! Thanks for your insurance enquiry. 🛡️`,
                                training: `Hi ${selectedLead.name}! Thanks for your course enquiry! 📚`,
                                healthcare: `Hi ${selectedLead.name}! Thanks for reaching out! 🏥`,
                                automobile: `Hi ${selectedLead.name}! Thanks for your enquiry! 🚗`,
                                general: `Hi ${selectedLead.name}! Thank you for your enquiry! 😊`
                              };
                              const message = templates[businessType] || templates.general;
                              const waLink = `https://wa.me/${cleanPhone}?text=${encodeURIComponent(message)}`;
                              window.open(waLink, '_blank');
                            }}
                          >
                            <MessageCircle className="w-4 h-4 mr-2" />
                            WhatsApp
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            className="flex-1"
                            onClick={() => window.open(`tel:${selectedLead.phone}`, '_self')}
                          >
                            <PhoneCall className="w-4 h-4 mr-2" />
                            Call
                          </Button>
                          <Button
                            size="sm"
                            variant="outline"
                            className="flex-1"
                            onClick={() => window.open(`mailto:${selectedLead.email}`, '_self')}
                          >
                            <Mail className="w-4 h-4 mr-2" />
                            Email
                          </Button>
                        </div>
                      </div>
                    )}

                    {/* Timeline Events */}
                    {selectedLead.timeline?.length > 0 && (
                      <div className="pt-4 border-t border-gray-200">
                        <label className="text-sm font-medium text-gray-500 mb-3 block">Activity Timeline</label>
                        <div className="space-y-3 max-h-[200px] overflow-y-auto">
                          {selectedLead.timeline.slice().reverse().map((event, idx) => {
                            const icons = {
                              automation_started: '🤖',
                              email_sent: '📧',
                              whatsapp_sent: '💬',
                              whatsapp_link_ready: '💬',
                              call_initiated: '📞',
                              call_completed: '📞',
                              stage_changed: '🔄',
                              score_changed: '⭐'
                            };
                            const icon = icons[event.type] || '📌';
                            const time = event.timestamp ? new Date(event.timestamp).toLocaleString() : '';
                            
                            return (
                              <div key={idx} className="flex items-start gap-3 p-2 rounded-lg bg-gray-50 hover:bg-gray-100">
                                <span className="text-lg">{icon}</span>
                                <div className="flex-1">
                                  <p className="text-sm text-gray-800">{event.message}</p>
                                  <p className="text-xs text-gray-500">{time}</p>
                                  {event.wa_link && (
                                    <a
                                      href={event.wa_link}
                                      target="_blank"
                                      rel="noopener noreferrer"
                                      className="mt-1 inline-flex items-center gap-1 text-xs text-green-600 hover:text-green-700"
                                    >
                                      <MessageCircle className="w-3 h-3" />
                                      Open WhatsApp Chat →
                                    </a>
                                  )}
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {activeTab === 'calls' && (
                  <div>
                    {loadingCalls ? (
                      <div className="flex items-center justify-center py-12">
                        <div className="animate-spin rounded-full h-8 w-8 border-2 border-indigo-500 border-t-transparent"></div>
                      </div>
                    ) : leadCalls.length === 0 ? (
                      <div className="text-center py-12">
                        <PhoneCall className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                        <h3 className="text-lg font-semibold text-gray-600 mb-2">No Calls Yet</h3>
                        <p className="text-gray-500 text-sm mb-4">
                          No calls made yet. Click the phone icon to start an AI call!
                        </p>
                        {selectedLead?.phone && (
                          <Button
                            className="bg-blue-600 hover:bg-blue-700"
                            onClick={() => {
                              setShowLeadDetail(false);
                              openCallModal(selectedLead);
                            }}
                          >
                            <PhoneCall className="w-4 h-4 mr-2" />
                            Start AI Call
                          </Button>
                        )}
                      </div>
                    ) : (
                      <div className="space-y-4">
                        {leadCalls.map((call) => (
                          <CallRecordingCard 
                            key={call.id} 
                            call={call} 
                            formatDuration={formatDuration}
                            formatDateTime={formatDateTime}
                            getCallStatusBadge={getCallStatusBadge}
                          />
                        ))}
                      </div>
                    )}
                  </div>
                )}

                {activeTab === 'documents' && (
                  <div>
                    {loadingDocs ? (
                      <div className="flex items-center justify-center py-12">
                        <div className="animate-spin rounded-full h-8 w-8 border-2 border-indigo-500 border-t-transparent"></div>
                      </div>
                    ) : leadDocuments.length === 0 ? (
                      <div className="text-center py-12">
                        <FileText className="w-16 h-16 text-gray-300 mx-auto mb-4" />
                        <h3 className="text-lg font-semibold text-gray-600 mb-2">No Documents</h3>
                        <p className="text-gray-500 text-sm mb-4">
                          No documents are attached to this lead yet.
                        </p>
                        <Button
                          variant="outline"
                          onClick={() => {
                            setShowLeadDetail(false);
                            navigate('/doc-analyzer');
                          }}
                        >
                          <FileText className="w-4 h-4 mr-2" />
                          Analyze a Document
                        </Button>
                      </div>
                    ) : (
                      <div className="space-y-3">
                        {leadDocuments.map((doc) => (
                          <Card key={doc.id} className="hover:shadow-md transition-shadow">
                            <CardContent className="p-4">
                              <div className="flex items-center justify-between">
                                <div className="flex items-center gap-3">
                                  <div className="w-10 h-10 rounded-lg bg-indigo-100 flex items-center justify-center">
                                    <FileText className="w-5 h-5 text-indigo-600" />
                                  </div>
                                  <div>
                                    <p className="font-medium text-gray-900">
                                      {doc.analysis?.title || doc.filename}
                                    </p>
                                    <p className="text-xs text-gray-500">
                                      {doc.analysis?.document_type || 'Document'} • {formatDate(doc.created_at)}
                                    </p>
                                  </div>
                                </div>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => {
                                    setShowLeadDetail(false);
                                    navigate(`/doc-analyzer?view=${doc.id}`);
                                  }}
                                >
                                  <ExternalLink className="w-4 h-4" />
                                </Button>
                              </div>
                            </CardContent>
                          </Card>
                        ))}
                      </div>
                    )}
                  </div>
                )}
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Call Modal */}
      <CallModal 
        isOpen={showCallModal} 
        onClose={() => {
          setShowCallModal(false);
          setCallModalLead(null);
        }} 
        lead={callModalLead} 
      />

      {/* Bulk WhatsApp Modal */}
      <BulkWhatsAppModal
        isOpen={showBulkWhatsApp}
        onClose={() => setShowBulkWhatsApp(false)}
        selectedLeads={leads.filter(l => selectedLeadIds.includes(l.id))}
      />

      {/* Single Lead WhatsApp Compose Modal */}
      <WhatsAppComposeModal
        isOpen={showWhatsAppCompose}
        onClose={() => {
          setShowWhatsAppCompose(false);
          setWhatsAppLead(null);
        }}
        lead={whatsAppLead}
      />
    </div>
  );
};

export default LeadsEnhanced;
